import { ChangeDetectorRef, Component, ElementRef, HostListener, inject, Input, Optional, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { CommonModule, NgClass } from '@angular/common';
import { SpecialCharacterHelper } from 'shared-lib/services/special-character-helper'
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import { DataService } from 'shared-lib';
import { catchError, firstValueFrom, of } from 'rxjs';
import { GlobalService } from '../../../global.service';
import { MatTreeModule } from '@angular/material/tree';
import { environment } from 'environments/environment';
import { MatIconModule } from '@angular/material/icon';
import { SharedModule } from '../../../shared.module';
import { CategoryTreeComponent } from './category-tree/category-tree.component';
import { PRODUCT_TYPE } from 'shared-lib/constants/app-constant';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CustomEditorComponent } from 'custom-editor';
import { ElementorEditor } from './elementor-editor/elementor-editor';

import { QuillEditorComponent, QuillModule } from 'ngx-quill';
import Quill from 'quill';

import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

interface FoodNode {
  name: string;
  children?: FoodNode[];
}

interface ProductGalleryImageEntry {
  source: 'existing' | 'new';
  url: string;
  mediaId: number | null;
  file?: File;
}

@Component({
  selector: 'app-add-customer',
  imports: [ReactiveFormsModule, QuillModule, MatTreeModule, MatIconModule, CategoryTreeComponent, NgClass, CommonModule, FormsModule, CustomEditorComponent, ElementorEditor],
  templateUrl: './add-product.html',
  styleUrl: './add-product.scss',
})
export class AddProduct {
   public dataService:any= inject(DataService);
  specialCharacterHelper = inject(SpecialCharacterHelper)
  childrenAccessor = (node: FoodNode) => node.children ?? [];
  @ViewChild('galleryInput') galleryInput!: ElementRef<HTMLInputElement>;
  @ViewChild('descriptionImageGallery') descriptionImageGallery!: ElementRef<HTMLInputElement>;
  @ViewChild('quillEditor') quillEditor!: QuillEditorComponent;
  @ViewChild('productDescriptionQuill') productDescriptionQuill!: QuillEditorComponent;
  
  elementorMode = {
    shortDescription: false,
    productDescription: false,
    features: false,
    productDescriptionImageGallery: false
  };

  currentEditingField: keyof typeof this.elementorMode | null = null;
  tempElementorValue: string = '';
  private modalService = inject(NgbModal);
  private sanitizer = inject(DomSanitizer);

  getSafeHtml(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html || '');
  }

  toggleElementorMode(field: keyof typeof this.elementorMode) {
    this.elementorMode[field] = !this.elementorMode[field];
  }

  openElementorBuilder(field: keyof typeof this.elementorMode, modalTemplate: any) {
    this.currentEditingField = field;
    this.tempElementorValue = this.productDetails.get(field)?.value || '';
    
    this.modalService.open(modalTemplate, {
      size: 'xl',
      centered: true,
      scrollable: true,
      backdrop: 'static'
    }).result.then((result) => {
      if (result === 'save' && this.currentEditingField) {
        this.elementorMode[this.currentEditingField] = true;
        this.productDetails.get(this.currentEditingField)?.setValue(this.tempElementorValue);
        this.productDetails.get(this.currentEditingField)?.markAsDirty();
      }
      this.cd.detectChanges();
    }, () => {
      this.cd.detectChanges();
    });
  }
  @Input() data: any = {
    price_data: {},
    shipping_info: {},
    shipping_config: {},
    offer: {},

  };

  private resolveMediaModuleId(preferredModuleId?: number | string): string {
    if (preferredModuleId !== undefined && preferredModuleId !== null && String(preferredModuleId).trim() !== '') {
      return String(preferredModuleId);
    }

    const currentModuleId = this.data?.item?.id;
    if (currentModuleId !== undefined && currentModuleId !== null && String(currentModuleId).trim() !== '') {
      return String(currentModuleId);
    }

    return '0';
  }

  private buildMediaUploadFormData(file: File, type: string, moduleId?: number | string): FormData {
    const formData = new FormData();
    formData.append('files', file);
    formData.append('module', 'product');
    formData.append('module_id', this.resolveMediaModuleId(moduleId));
    formData.append('type', type);
    return formData;
  }

  handleImageUpload = async (file: File): Promise<string> => {
    if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
      throw new Error('Unsupported file type');
    }
    if (file.size > 1_000_000) {
      throw new Error('File size too large');
    }

    const formData = this.buildMediaUploadFormData(file, 'product_image_description');

    const extractUrl = (res: any): string | undefined =>
      res?.data?.[0]?.url ||
      res?.data?.data?.[0]?.url ||
      res?.data?.url ||
      res?.data?.data?.url ||
      res?.url ||
      res?.data?.path ||
      res?.data?.[0]?.path ||
      res?.data?.data?.[0]?.path;

    const findImageUrlDeep = (input: any, seen = new Set<any>()): string | undefined => {
      if (input == null || typeof input === 'boolean') return undefined;
      if (typeof input === 'string') {
        if (/\.(png|jpe?g|webp|gif|svg)(\?.*)?$/i.test(input)) return input;
        return undefined;
      }
      if (typeof input !== 'object') return undefined;
      if (seen.has(input)) return undefined;
      seen.add(input);

      if (Array.isArray(input)) {
        for (const item of input) {
          const found = findImageUrlDeep(item, seen);
          if (found) return found;
        }
        return undefined;
      }

      for (const key of Object.keys(input)) {
        const found = findImageUrlDeep(input[key], seen);
        if (found) return found;
      }
      return undefined;
    };

    const normalizeUrl = (raw: string) => {
      if (/^https?:\/\//i.test(raw)) {
        if (raw.startsWith('http://') && environment.DOMAIN.startsWith('https://')) {
          return raw.replace(/^http:\/\//i, 'https://');
        }
        return raw;
      }
      return `${environment.DOMAIN.replace(/\/$/, '')}/${raw.replace(/^\//, '')}`;
    };

    let lastResponse: any = null;
    const tryUpload = async (endpoint: string) => {
      const res: any = await firstValueFrom(this.dataService.postForm(endpoint, formData));
      lastResponse = res;
      return extractUrl(res);
    };

    let imageUrl: string | undefined;
    try {
      imageUrl = await tryUpload('gallery');
    } catch (err) {
      imageUrl = undefined;
    }

    if (!imageUrl) {
      try {
        imageUrl = await tryUpload('media/upload');
      } catch (err) {
        imageUrl = undefined;
      }
    }

    if (!imageUrl && lastResponse) {
      imageUrl = findImageUrlDeep(lastResponse);
    }

    if (!imageUrl) {
      console.error('Image upload failed: no URL returned', lastResponse);
      throw new Error('Upload failed');
    }

    return normalizeUrl(imageUrl);
  };
  editorTheme: 'light' | 'dark' = 'light';
  modulesNoImage = {
    toolbar: [
      // --- FONT FAMILY & SIZE ---
      [
        {
          font: [
            'sans-serif',
            'serif',
            'monospace',
            'roboto',
            'lato',
            'poppins',
            'montserrat'
          ]
        },
        { size: ['small', false, 'large', 'huge'] }
      ],

      // --- HEADINGS (H1–H6) ---
      [
        { header: [1, 2, 3, 4, 5, 6, false] }
      ],

      // --- TEXT STYLE ---
      ['bold', 'italic', 'underline', 'strike'],
      [{ color: [] }, { background: [] }],
      [{ script: 'sub' }, { script: 'super' }],

      // --- BLOCK ELEMENTS ---
      ['blockquote', 'code-block'],

      // --- LISTS & INDENTS ---
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ direction: 'rtl' }],

      // --- ALIGNMENT ---
      [{ align: [] }],

      // --- LINKS & MEDIA ---
      ['link', 'formula'],

      // --- UTILS ---
      ['clean']
    ]
  };




  // modulesWithImage = {
  //   htmlEditButton: {
  //     debug: true, // Developers love logs
  //     msg: "Edit HTML", // Tooltip
  //     okText: "Save",
  //   },
  //   toolbar: [
  //     // --- TEXT STYLE ---
  //     [{ 'font': [] }, { 'size': ['small', false, 'large', 'huge'] }],
  //     ['bold', 'italic', 'underline', 'strike'],
  //     [{ 'color': [] }, { 'background': [] }],
  //     [{ 'script': 'sub' }, { 'script': 'super' }],

  //     // --- HEADER & QUOTES ---
  //     [{ 'header': 1 }, { 'header': 2 }, 'blockquote',],

  //     // --- LISTS & INDENTS ---
  //     [{ 'list': 'ordered' }, { 'list': 'bullet' }],
  //     [{ 'indent': '-1' }, { 'indent': '+1' }],
  //     [{ 'direction': 'rtl' }],

  //     // --- ALIGNMENT ---
  //     [{ 'align': [] }],

  //     // --- LINKS & MEDIA ---
  //     ['link', 'image', 'formula'],
  //     // ['htmlEditButton'],
  //     // --- UTILS ---
  //     ['clean']
  //   ],
  //   imageHandler: {
  //     upload: (file: File) => {

  //       return new Promise((resolve, reject) => {

  //         // 1. Validation Logic
  //         if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
  //           console.info('Unsupported type');
  //           return reject('Unsupported type');
  //         }
  //         if (file.size > 1000000) {
  //           console.info('Size');
  //           return reject('Size too large (Max 1MB)');
  //         }

  //         // 2. Call the Service
  //         const formData = new FormData();
  //         formData.append('files', file);
  //         formData.append('type', 'product_image_description');

  //         this.dataService
  //           .postForm('gallery', formData)
  //           .pipe(
  //             catchError((err) => {
  //               return of(null);
  //             })
  //           )
  //           .subscribe((res: any) => {
  //             console.log('Response:', res);
  //             resolve(res.data[0].url);

  //           });

  //       });
  //     },
  //     accepts: ['png', 'jpg', 'jpeg', 'jfif']
  //   } as Options,




  // };

  modulesWithImage = {
    //   htmlEditButton: {
    //   debug: true, // Developers love logs
    //   msg: "Edit HTML", // Tooltip
    //   okText: "Save",
    // },
    toolbar: {
      container: [
        [{
          font: [
            'sans-serif',
            'serif',
            'monospace',
            'roboto',
            'lato',
            'poppins',
            'montserrat'
          ]
        },
        { size: ['small', false, 'large', 'huge'], }],
        [
          { header: [1, 2, 3, 4, 5, 6, false] }
        ],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ header: 1 }, { header: 2 }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ align: [] }],
        ['link', 'image'],
        ['clean'],
      ],
      handlers: {
        image: () => { },
      },
    },
  };


  dataSource = [];
  inputControlName: any;

  // Image upload handler is provided to the custom editor via [uploadFn].

  toggleTheme() {
    this.editorTheme = this.editorTheme === 'light' ? 'dark' : 'light';
  }
  hasChild = (_: number, node: FoodNode) => !!node.children && node.children.length > 0;
  productDetails!: FormGroup;
  inventoryForm!: FormGroup;
  // priceForm!: FormGroup;
  shippingInfoForm!: FormGroup;
  videoForm!: FormGroup;
  shippingConfigForm!: FormGroup;
  offerForm!: FormGroup;
  seoForm!: FormGroup;
  categoryForm!: FormGroup;
  publishForm!: FormGroup;
  productOptionData!: FormGroup;
  productMultipleOptionForm!: FormGroup;
  tagsForm!: FormGroup;
  commonTags = ['mobile', 'tab', 'watch', 't-shirt', 'shirt', 'book', 'monitor', 'cloth'];
  categories = [
    {
      id: 'catFinance',
      name: 'Finance',
      subCategories: [
        { id: 'catBanking', name: 'Banking' },
        {
          id: 'catAccounting',
          name: 'Accounting',
          subCategories: [{ id: 'catBangladeshBank', name: 'BangladeshBank' }],
        },
      ],
    },
    {
      id: 'catFashion',
      name: 'Fashion & Clothing',
      subCategories: [
        { id: 'catTShirt', name: 't-Shirt' },
        {
          id: 'catShirt',
          name: 'Shirt',
          subCategories: [{ id: 'catCasualShirt', name: 'Casual Shirt' }],
        },
      ],
    },
    { id: 'catBag', name: 'Bag' },
    { id: 'catMonitor', name: 'Monitor' },
    { id: 'catKeyboard', name: 'Keyboard' },
    { id: 'catMouse', name: 'Mouse' },
  ];
  productMediaSection!: FormGroup;
  thumbFile: any;
  galleryFiles: any;
  thumbPreview: any = [];
  productInventrySection!: FormGroup;
  priceSection!: FormGroup;
  shippingInfoSection!: FormGroup;
  productAttributesForm!: FormGroup;
  variantsReactiveForm!: FormGroup;

  categoryListData: any = {
    isSelectedCategory: {},
    categories: []
  };
  parentId!: Number;
  selectedThumbImg: any;
  thumbGallery: any = [];
  permaLink: any;
  isInputShow: boolean = false;
  domain: string = '';
  productStatus: any = [];
  wrongDiscount: boolean = false;
  selectProductDesciptionImageGallery: File[] = [];
  prodDescriptionImageGallery: any = [];
  selectedFile: File[] = [];
  private galleryImageEntries: ProductGalleryImageEntry[] = [];
  private removedGalleryMediaIds = new Set<number>();
  private removedGalleryImageUrls = new Set<string>();
  statusMap: any = [];
  isUpdateproduct: boolean = false;
  finalpriceObj: any;
  showHtmlEditor = false;
  htmlControl = new FormControl('');

  // Variable Products
  allAttributes: any[] = [];
  selectedAttributesForVariations: any[] = [];
  variableCombinations: any[] = [];
  private variantsMergeCache: any[] = [];
  private hasAutoPreviewedUpdateCombinations = false;
  private isAutoPreviewInProgress = false;
  private colorAttributeValueIds = new Set<string>();
  private uploadingVariantImageIndexes = new Set<number>();
  private pendingVariantImageFiles = new Map<string, File>();
  showVariantTable = false;
  productType: 'simple' | 'variable' = 'simple';

  constructor(
    private fb: FormBuilder,
    private globalService: GlobalService,
    private cd: ChangeDetectorRef,
    @Optional() public activeModal: NgbActiveModal
  ) {
    this.productStatus = PRODUCT_TYPE;

  }
  openHtmlEditor(quillName: any) {
    this.inputControlName = quillName;
    this.htmlControl.setValue(
      this.productDetails.get(quillName)?.value || ''
    );

    this.showHtmlEditor = true;
  }

  closeHtmlEditor() {
    this.showHtmlEditor = false;
  }

  saveHtml() {
    this.productDetails
      .get(this.inputControlName)
      ?.setValue(this.htmlControl.value);

    this.showHtmlEditor = false;
  }
  onGetId(id: number) {
    this.parentId = id;
  }

  loadAllAttributes() {
    this.dataService.get('attributes').subscribe((res: any) => {
      if (res.data && Array.isArray(res.data.data)) {
        this.allAttributes = res.data.data;
      } else if (Array.isArray(res.data)) {
        this.allAttributes = res.data;
      } else {
        this.allAttributes = res || [];
      }
      this.colorAttributeValueIds = this.collectColorAttributeValueIds(this.allAttributes);
      this.syncSelectedAttributesFromExistingVariants();
      if (this.variantsReactiveForm && this.variantsFormArray.length > 0) {
        this.applyVariantsFromSource(this.variantsFormArray.getRawValue());
      } else if (Array.isArray(this.variableCombinations) && this.variableCombinations.length > 0) {
        this.applyVariantsFromSource(this.variableCombinations);
      }
      this.tryAutoGeneratePreviewCombinationsForUpdate();
      this.cd.detectChanges();
    });
  }

  private buildSelectedVariationAttribute(attr: any, existingAttr?: any) {
    const validValueIds = new Set((attr.values || []).map((value: any) => String(value.id)));
    const existingValueIds = Array.isArray(existingAttr?.value_ids) ? existingAttr.value_ids : [];
    const valueIds = existingValueIds.filter((id: any) => validValueIds.has(String(id)));

    return {
      attribute_id: attr.id,
      name: attr.name,
      type: attr.type,
      value_ids: valueIds,
      values: attr.values || []
    };
  }

  private initializeVariantsReactiveForm() {
    this.variantsReactiveForm = this.fb.group({
      variants: this.fb.array([])
    });
  }

  get variantsFormArray(): FormArray {
    return this.variantsReactiveForm.get('variants') as FormArray;
  }

  private isColorAttribute(attr: any): boolean {
    const normalizedName = String(attr?.name ?? '').trim().toLowerCase();
    const normalizedType = String(attr?.type ?? '').trim().toLowerCase();

    return normalizedName === 'color' || normalizedType === 'color' || normalizedType === 'color_swatch';
  }

  private collectColorAttributeValueIds(attributes: any[]): Set<string> {
    const colorValueIds = new Set<string>();

    (attributes || []).forEach((attr: any) => {
      if (!this.isColorAttribute(attr)) {
        return;
      }

      (attr?.values || []).forEach((value: any) => {
        const normalizedId = String(value?.id ?? '').trim();
        if (normalizedId !== '') {
          colorValueIds.add(normalizedId);
        }
      });
    });

    return colorValueIds;
  }

  private normalizeVariantImageId(rawValue: any): number | null {
    if (rawValue === null || rawValue === undefined || rawValue === '') {
      return null;
    }

    const parsedValue = Number(rawValue);
    if (Number.isNaN(parsedValue) || parsedValue <= 0) {
      return null;
    }

    return parsedValue;
  }

  private normalizeMediaType(type: any): string {
    return String(type ?? '').trim().toLowerCase();
  }

  private isGalleryMediaType(type: any): boolean {
    const normalizedType = this.normalizeMediaType(type);
    return normalizedType === 'gallery' || normalizedType === 'thumbgallery';
  }

  private extractProductMediaId(media: any): number | null {
    const candidateIds = [
      media?.media_id,
      media?.image_id,
      media?.id,
      media?.mediaId,
      media?.imageId
    ];

    for (const candidateId of candidateIds) {
      const normalizedId = this.normalizeVariantImageId(candidateId);
      if (normalizedId !== null) {
        return normalizedId;
      }
    }

    return null;
  }

  private extractProductMediaUrl(media: any): string {
    const candidateUrl =
      media?.url ??
      media?.path ??
      media?.image_url ??
      media?.image?.url ??
      media?.image?.path ??
      '';

    return typeof candidateUrl === 'string' ? candidateUrl.trim() : '';
  }

  private syncThumbGalleryFromEntries() {
    this.thumbGallery = this.galleryImageEntries.map((entry: ProductGalleryImageEntry) => entry.url);
  }

  private rebuildGalleryInputFileList() {
    if (!this.galleryInput) {
      return;
    }

    if (!Array.isArray(this.selectedFile) || this.selectedFile.length === 0) {
      this.galleryInput.nativeElement.value = '';
      return;
    }

    const dataTransfer = new DataTransfer();
    this.selectedFile.forEach((file: File) => dataTransfer.items.add(file));
    this.galleryInput.nativeElement.files = dataTransfer.files;
  }

  private removeSelectedFileByReference(file?: File) {
    if (!file) {
      return;
    }

    const fileIndex = this.selectedFile.findIndex((item: File) => item === file);
    if (fileIndex > -1) {
      this.selectedFile.splice(fileIndex, 1);
    }
  }

  private initializeGalleryEntries(images: any[] = []) {
    const galleryImages = (images || []).filter((img: any) => this.isGalleryMediaType(img?.type));
    const entries: ProductGalleryImageEntry[] = [];

    galleryImages.forEach((img: any) => {
      const url = this.extractProductMediaUrl(img);
      if (url === '') {
        return;
      }

      entries.push({
        source: 'existing',
        url,
        mediaId: this.extractProductMediaId(img)
      });
    });

    this.galleryImageEntries = entries;

    this.selectedFile = [];
    this.syncThumbGalleryFromEntries();
    this.rebuildGalleryInputFileList();
  }

  private rememberRemovedGalleryUrl(url: string) {
    const normalizedUrl = String(url ?? '').trim();
    if (normalizedUrl === '') {
      return;
    }

    this.removedGalleryImageUrls.add(normalizedUrl);
    if (!/^https?:\/\//i.test(normalizedUrl) && !/^(data:|blob:)/i.test(normalizedUrl)) {
      this.removedGalleryImageUrls.add(this.buildAbsoluteImageUrl(normalizedUrl));
    }
  }

  private getRemovedGalleryMediaIds(): number[] {
    return Array.from(this.removedGalleryMediaIds.values())
      .map((id: number) => this.normalizeVariantImageId(id))
      .filter((id: number | null): id is number => id !== null);
  }

  private getRemovedGalleryUrls(): string[] {
    return Array.from(this.removedGalleryImageUrls.values()).filter(
      (url: string) => typeof url === 'string' && url.trim() !== ''
    );
  }

  private async callMediaDeleteEndpoint(
    endpoint: string,
    moduleId: number | string,
    mediaId: number,
    method: 'delete' | 'post'
  ): Promise<boolean> {
    try {
      const payload = {
        id: mediaId,
        media_id: mediaId,
        module: 'product',
        module_id: this.resolveMediaModuleId(moduleId)
      };

      const response: any = await firstValueFrom(
        (method === 'delete'
          ? this.dataService.delete(endpoint)
          : this.dataService.post(payload, endpoint)
        ).pipe(
          catchError(() => of(null))
        )
      );

      if (!response) {
        return false;
      }

      if (response?.error || response?.success === false) {
        return false;
      }

      return true;
    } catch (error) {
      return false;
    }
  }

  private async deleteGalleryMediaById(mediaId: number, moduleId: number | string): Promise<boolean> {
    const deleteEndpoints = [
      { endpoint: `media/${mediaId}`, method: 'delete' as const },
      { endpoint: `gallery/${mediaId}`, method: 'delete' as const },
      { endpoint: 'media/delete', method: 'post' as const },
      { endpoint: 'gallery/delete', method: 'post' as const },
      { endpoint: 'media/remove', method: 'post' as const }
    ];

    for (const attempt of deleteEndpoints) {
      const deleted = await this.callMediaDeleteEndpoint(
        attempt.endpoint,
        moduleId,
        mediaId,
        attempt.method
      );
      if (deleted) {
        return true;
      }
    }

    return false;
  }

  private async syncRemovedGalleryMedia(moduleId: number | string): Promise<{ removed: number; failedIds: number[] }> {
    const removedMediaIds = this.getRemovedGalleryMediaIds();
    if (removedMediaIds.length === 0) {
      return { removed: 0, failedIds: [] };
    }

    const failedIds: number[] = [];
    let removed = 0;

    for (const mediaId of removedMediaIds) {
      const deleted = await this.deleteGalleryMediaById(mediaId, moduleId);
      if (deleted) {
        removed++;
        this.removedGalleryMediaIds.delete(mediaId);
      } else {
        failedIds.push(mediaId);
      }
    }

    if (failedIds.length === 0) {
      this.removedGalleryImageUrls.clear();
    }

    return { removed, failedIds };
  }

  private buildMediaSectionPayload(moduleId?: number | string) {
    return {
      thumbFile: this.thumbFile,
      galleryFiles: this.galleryFiles,
      removed_gallery_media_ids: this.getRemovedGalleryMediaIds(),
      removed_gallery_urls: this.getRemovedGalleryUrls(),
      module: 'product',
      module_id: this.resolveMediaModuleId(moduleId)
    };
  }

  private getVariantSkuSuffix(variant: any): string {
    return String(variant?.sku_suffix ?? variant?.skuSuffix ?? variant?.sku ?? '').trim();
  }

  private normalizeVariantStatus(rawStatus: any): boolean {
    if (rawStatus === null || rawStatus === undefined || rawStatus === '') {
      return true;
    }

    if (typeof rawStatus === 'boolean') {
      return rawStatus;
    }

    if (typeof rawStatus === 'number') {
      return rawStatus === 1;
    }

    const normalized = String(rawStatus).trim().toLowerCase();
    if (normalized === 'false' || normalized === '0' || normalized === 'inactive' || normalized === 'disabled') {
      return false;
    }
    if (normalized === 'true' || normalized === '1' || normalized === 'active' || normalized === 'enabled') {
      return true;
    }

    return true;
  }

  private extractVariantImageId(variant: any): number | null {
    const candidateIds = [
      variant?.image_id,
      variant?.media_id,
      variant?.imageId,
      variant?.mediaId,
      variant?.image?.image_id,
      variant?.image?.media_id,
      variant?.image?.id,
      variant?.media?.image_id,
      variant?.media?.media_id,
      variant?.media?.id
    ];

    for (const candidateId of candidateIds) {
      const normalizedId = this.normalizeVariantImageId(candidateId);
      if (normalizedId !== null) {
        return normalizedId;
      }
    }

    return null;
  }

  private extractVariantImagePreviewUrl(row: any): string {
    const rawUrl =
      row?.variant_image_url ||
      row?.image_preview_url ||
      row?.image_url ||
      row?.image?.url ||
      row?.image?.path ||
      row?.media?.url ||
      row?.media?.path ||
      '';

    if (typeof rawUrl !== 'string' || rawUrl.trim() === '') {
      return '';
    }

    if (/^(data:|blob:)/i.test(rawUrl)) {
      return rawUrl;
    }

    return this.buildAbsoluteImageUrl(rawUrl);
  }

  private findImageByIdInObject(input: any, targetImageId: number, seen = new Set<any>()): any | null {
    if (input == null || typeof input !== 'object') {
      return null;
    }

    if (seen.has(input)) {
      return null;
    }
    seen.add(input);

    if (Array.isArray(input)) {
      for (const item of input) {
        const found = this.findImageByIdInObject(item, targetImageId, seen);
        if (found) {
          return found;
        }
      }
      return null;
    }

    const candidateIds = [
      input?.id,
      input?.image_id,
      input?.media_id,
      input?.imageId,
      input?.mediaId
    ];
    const hasMatchingId = candidateIds.some(
      (candidateId: any) => this.normalizeVariantImageId(candidateId) === targetImageId
    );
    if (hasMatchingId) {
      const hasImageUrl =
        typeof input?.url === 'string' ||
        typeof input?.path === 'string' ||
        typeof input?.image_url === 'string' ||
        typeof input?.image?.url === 'string' ||
        typeof input?.image?.path === 'string';

      if (hasImageUrl) {
        return input;
      }
    }

    for (const value of Object.values(input)) {
      const found = this.findImageByIdInObject(value, targetImageId, seen);
      if (found) {
        return found;
      }
    }

    return null;
  }

  private resolveVariantImagePreviewUrl(row: any): string {
    const directPreviewUrl = this.extractVariantImagePreviewUrl(row);
    if (directPreviewUrl) {
      return directPreviewUrl;
    }

    const imageId = this.extractVariantImageId(row);
    if (imageId === null) {
      return '';
    }

    const lookupSources = [
      row?.image,
      row?.media,
      row,
      this.data?.item?.images,
      this.data?.item?.media,
      this.data?.item?.product_details,
      this.data?.item
    ];

    for (const source of lookupSources) {
      const matchedImage = this.findImageByIdInObject(source, imageId);
      if (!matchedImage) {
        continue;
      }

      const matchedUrl =
        matchedImage?.url ??
        matchedImage?.path ??
        matchedImage?.image_url ??
        matchedImage?.image?.url ??
        matchedImage?.image?.path ??
        '';

      if (typeof matchedUrl === 'string' && matchedUrl.trim() !== '') {
        if (/^(data:|blob:)/i.test(matchedUrl)) {
          return matchedUrl;
        }
        return this.buildAbsoluteImageUrl(matchedUrl.trim());
      }
    }

    return '';
  }

  private extractUploadedMediaUrl(response: any): string | undefined {
    const directUrl =
      response?.data?.[0]?.url ||
      response?.data?.data?.[0]?.url ||
      response?.data?.url ||
      response?.data?.data?.url ||
      response?.url ||
      response?.data?.path ||
      response?.data?.[0]?.path ||
      response?.data?.data?.[0]?.path;

    if (typeof directUrl === 'string' && directUrl.trim() !== '') {
      return directUrl;
    }

    return this.findImageUrlDeep(response?.data ?? response);
  }

  private findImageUrlDeep(input: any, seen = new Set<any>()): string | undefined {
    if (input == null || typeof input === 'boolean') {
      return undefined;
    }

    if (typeof input === 'string') {
      return /\.(png|jpe?g|webp|gif|svg)(\?.*)?$/i.test(input) ? input : undefined;
    }

    if (typeof input !== 'object') {
      return undefined;
    }

    if (seen.has(input)) {
      return undefined;
    }
    seen.add(input);

    if (Array.isArray(input)) {
      for (const item of input) {
        const foundUrl = this.findImageUrlDeep(item, seen);
        if (foundUrl) {
          return foundUrl;
        }
      }
      return undefined;
    }

    for (const key of Object.keys(input)) {
      const foundUrl = this.findImageUrlDeep(input[key], seen);
      if (foundUrl) {
        return foundUrl;
      }
    }

    return undefined;
  }

  private findMediaIdDeep(input: any, seen = new Set<any>()): number | null {
    if (input == null) {
      return null;
    }

    if (typeof input !== 'object') {
      return null;
    }

    if (seen.has(input)) {
      return null;
    }
    seen.add(input);

    if (Array.isArray(input)) {
      for (const item of input) {
        const foundId = this.findMediaIdDeep(item, seen);
        if (foundId !== null) {
          return foundId;
        }
      }
      return null;
    }

    const mediaLikeId = this.normalizeVariantImageId(
      input?.media_id ?? input?.mediaId ?? (input?.url || input?.path ? input?.id : null)
    );
    if (mediaLikeId !== null) {
      return mediaLikeId;
    }

    for (const key of ['media_id', 'mediaId', 'id']) {
      const matchedId = this.normalizeVariantImageId(input?.[key]);
      if (matchedId !== null) {
        return matchedId;
      }
    }

    for (const value of Object.values(input)) {
      const foundId = this.findMediaIdDeep(value, seen);
      if (foundId !== null) {
        return foundId;
      }
    }

    return null;
  }

  private extractUploadedMediaId(response: any): number | null {
    const candidateIds = [
      response?.data?.[0]?.media_id,
      response?.data?.[0]?.id,
      response?.data?.data?.[0]?.media_id,
      response?.data?.data?.[0]?.id,
      response?.data?.media_id,
      response?.data?.id,
      response?.data?.data?.media_id,
      response?.data?.data?.id,
      response?.media_id,
      response?.id
    ];

    for (const candidateId of candidateIds) {
      const normalizedId = this.normalizeVariantImageId(candidateId);
      if (normalizedId !== null) {
        return normalizedId;
      }
    }

    return this.findMediaIdDeep(response?.data?.data ?? response?.data ?? response);
  }

  private normalizeUploadedMediaUrl(rawUrl: string): string {
    if (/^https?:\/\//i.test(rawUrl)) {
      if (rawUrl.startsWith('http://') && environment.DOMAIN.startsWith('https://')) {
        return rawUrl.replace(/^http:\/\//i, 'https://');
      }
      return rawUrl;
    }

    return `${environment.DOMAIN.replace(/\/$/, '')}/${rawUrl.replace(/^\//, '')}`;
  }

  private async readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => resolve(String(reader.result ?? ''));
      reader.onerror = () => reject(new Error('Unable to read selected image'));

      reader.readAsDataURL(file);
    });
  }

  private async uploadVariantImageFile(file: File, moduleId?: number | string): Promise<{ imageId: number; imageUrl: string }> {
    if (!file.type || !file.type.toLowerCase().startsWith('image/')) {
      throw new Error('Please select a valid image file');
    }

    const formData = this.buildMediaUploadFormData(file, 'variant', moduleId);

    const response: any = await firstValueFrom(this.dataService.postForm('media/upload', formData));
    const imageId = this.extractUploadedMediaId(response);

    if (imageId === null) {
      console.error('Variant image upload failed: no media ID returned', response);
      throw new Error('Upload failed: no media ID returned');
    }

    const uploadedUrl = this.extractUploadedMediaUrl(response);

    return {
      imageId,
      imageUrl: uploadedUrl ? this.normalizeUploadedMediaUrl(uploadedUrl) : ''
    };
  }

  private rebuildVariantsFormArray(variants: any[]) {
    if (!this.variantsReactiveForm) {
      return;
    }

    const formArray = this.variantsFormArray;
    formArray.clear();

    (variants || []).forEach((variant: any) => {
      const normalizedSkuSuffix = this.getVariantSkuSuffix(variant);
      const normalizedAttributeIds = this.extractVariantAttributeValueIds(variant);
      const normalizedStatus = this.normalizeVariantStatus(
        variant?.is_active ?? variant?.status ?? variant?.isActive
      );
      const normalizedImageId = this.extractVariantImageId(variant);
      const previewUrl = this.resolveVariantImagePreviewUrl(variant);

      formArray.push(
        this.fb.group({
          variant_name: [this.resolveVariantName({ ...variant, sku_suffix: normalizedSkuSuffix, attribute_value_ids: normalizedAttributeIds }) || variant?.variant_name || ''],
          sku_suffix: [normalizedSkuSuffix],
          attribute_value_ids: [normalizedAttributeIds],
          price: [variant?.price ?? ''],
          stock: [variant?.stock ?? 0],
          is_active: [normalizedStatus],
          image_id: [normalizedImageId],
          image_preview_url: [previewUrl]
        })
      );
    });
    this.showVariantTable = formArray.length > 0;
  }

  private parseVariantsCandidate(candidate: any): any[] {
    if (Array.isArray(candidate)) {
      return candidate;
    }
    if (candidate && Array.isArray(candidate.data)) {
      return candidate.data;
    }
    if (candidate && candidate.data !== undefined) {
      const nestedData = this.parseVariantsCandidate(candidate.data);
      if (nestedData.length > 0) {
        return nestedData;
      }
    }
    if (typeof candidate === 'string') {
      try {
        const parsed = JSON.parse(candidate);
        return this.parseVariantsCandidate(parsed);
      } catch (error) {
        return [];
      }
    }
    return [];
  }

  private extractVariantsFromItem(item: any): any[] {
    const candidates = [
      item?.variants,
      item?.variant,
      item?.product_details?.variants,
      item?.attributes?.variants
    ];

    for (const candidate of candidates) {
      const parsed = this.parseVariantsCandidate(candidate);
      if (parsed.length > 0) {
        return parsed;
      }
    }
    return [];
  }

  private normalizeAttributeValueIds(rawValue: any): any[] {
    const toNormalizedId = (value: any) => {
      if (typeof value === 'number') {
        return value;
      }

      const text = String(value ?? '').trim();
      if (text === '') {
        return null;
      }

      const parsedNumber = Number(text);
      return Number.isNaN(parsedNumber) ? text : parsedNumber;
    };

    if (Array.isArray(rawValue)) {
      return rawValue
        .map((value) => toNormalizedId(value))
        .filter((value) => value !== null);
    }

    if (typeof rawValue === 'string') {
      const trimmed = rawValue.trim();
      if (trimmed === '') {
        return [];
      }

      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed
            .map((value) => toNormalizedId(value))
            .filter((value) => value !== null);
        }
      } catch (error) {
        // Falls back to comma-separated parsing below.
      }

      return trimmed
        .split(',')
        .map((value) => toNormalizedId(value))
        .filter((value) => value !== null);
    }

    const singleValue = toNormalizedId(rawValue);
    return singleValue === null ? [] : [singleValue];
  }

  private extractVariantAttributeValueIds(variant: any): any[] {
    return this.normalizeAttributeValueIds(
      variant?.attribute_value_ids ??
      variant?.attributeValues ??
      variant?.attribute_values ??
      variant?.attribute_value_id ??
      variant?.attribute_valueIds
    );
  }

  private isGenericVariantName(name: any): boolean {
    const normalized = String(name ?? '').trim();
    if (!normalized) {
      return false;
    }
    return /^variant\s*\d+$/i.test(normalized);
  }

  private getVariantNameFromAttributesDetail(attributesDetail: any): string {
    if (!Array.isArray(attributesDetail)) {
      return '';
    }

    return attributesDetail
      .map((detail: any) => detail?.value_name)
      .filter((name: any) => typeof name === 'string' && name.trim() !== '')
      .join(' - ');
  }

  private getVariantNameFromAttributeValueIds(attributeValueIds: any[]): string {
    if (!Array.isArray(attributeValueIds) || attributeValueIds.length === 0) {
      return '';
    }

    const valueNameById = new Map<string, string>();
    (this.allAttributes || []).forEach((attr: any) => {
      (attr?.values || []).forEach((value: any) => {
        const key = String(value?.id ?? '').trim();
        const valueLabel = String(value?.value ?? '').trim();
        if (key !== '' && valueLabel !== '' && !valueNameById.has(key)) {
          valueNameById.set(key, valueLabel);
        }
      });
    });

    const valueNames = attributeValueIds
      .map((id: any) => valueNameById.get(String(id)))
      .filter((name: any) => typeof name === 'string' && name.trim() !== '');

    return valueNames.join(' - ');
  }

  private resolveVariantName(variant: any): string {
    const rawVariantName = String(variant?.variant_name ?? '').trim();
    const skuSuffix = String(variant?.sku_suffix ?? '').trim();
    const attributeValueIds = this.normalizeAttributeValueIds(variant?.attribute_value_ids);
    const nameFromDetails = this.getVariantNameFromAttributesDetail(variant?.attributes_detail);
    const nameFromValueIds = this.getVariantNameFromAttributeValueIds(attributeValueIds);
    const meaningfulName = nameFromDetails || nameFromValueIds || skuSuffix;

    if (rawVariantName && (!this.isGenericVariantName(rawVariantName) || !meaningfulName)) {
      return rawVariantName;
    }

    return meaningfulName || rawVariantName;
  }

  private getVariantCombinationKey(row: any): string {
    const normalizedIds = this.normalizeAttributeValueIds(row?.attribute_value_ids)
      .map((id: any) => String(id))
      .sort();

    if (normalizedIds.length > 0) {
      return normalizedIds.join('|');
    }

    const skuSuffix = String(row?.sku_suffix ?? '').trim().toLowerCase();
    return skuSuffix ? `sku:${skuSuffix}` : '';
  }

  private getCurrentVariantsSnapshot(): any[] {
    const variantsSource =
      this.variantsReactiveForm && this.variantsFormArray.length > 0
        ? this.variantsFormArray.getRawValue()
        : Array.isArray(this.variableCombinations)
          ? this.variableCombinations
          : [];

    return this.normalizeVariantCombinations(variantsSource);
  }

  private findBestVariantMatch(attributeValueIds: any[], existingVariants: any[]) {
    if (!Array.isArray(attributeValueIds) || attributeValueIds.length === 0) {
      return null;
    }

    const targetSet = new Set(attributeValueIds.map((id: any) => String(id)));
    let bestMatch: any = null;
    let bestScore = -1;

    existingVariants.forEach((variant: any) => {
      const candidateIds = this.normalizeAttributeValueIds(variant?.attribute_value_ids);
      if (candidateIds.length === 0) {
        return;
      }

      const candidateSet = new Set(candidateIds.map((id: any) => String(id)));
      const intersection = candidateIds.filter((id: any) => targetSet.has(String(id))).length;
      const candidateIsSubsetOfTarget = intersection === candidateSet.size;
      const targetIsSubsetOfCandidate = intersection === targetSet.size;

      if (!candidateIsSubsetOfTarget && !targetIsSubsetOfCandidate) {
        return;
      }

      if (intersection > bestScore) {
        bestScore = intersection;
        bestMatch = variant;
      }
    });

    return bestMatch;
  }

  private mergeVariantsWithExistingData(nextVariants: any[], existingVariants: any[]) {
    if (!Array.isArray(nextVariants) || nextVariants.length === 0) {
      return [];
    }

    const normalizedExisting = this.normalizeVariantCombinations(existingVariants || []);
    const existingByKey = new Map<string, any>();
    normalizedExisting.forEach((variant: any) => {
      const key = this.getVariantCombinationKey(variant);
      if (key) {
        existingByKey.set(key, variant);
      }
    });

    return nextVariants.map((variant: any) => {
      const attributeValueIds = this.normalizeAttributeValueIds(variant?.attribute_value_ids);
      const key = this.getVariantCombinationKey({ attribute_value_ids: attributeValueIds, sku_suffix: variant?.sku_suffix });
      const exactMatchedVariant = key ? existingByKey.get(key) : null;
      const matchedVariant = exactMatchedVariant || this.findBestVariantMatch(attributeValueIds, normalizedExisting);
      const isExactMatch = !!exactMatchedVariant;

      if (!matchedVariant) {
        return variant;
      }

      const mergedVariant = {
        ...variant,
        sku_suffix: isExactMatch
          ? this.getVariantSkuSuffix(matchedVariant) || this.getVariantSkuSuffix(variant) || ''
          : this.getVariantSkuSuffix(variant) || this.getVariantSkuSuffix(matchedVariant) || '',
        price: matchedVariant?.price ?? variant?.price ?? '',
        stock: matchedVariant?.stock ?? variant?.stock ?? 0,
        is_active: this.normalizeVariantStatus(
          matchedVariant?.is_active ?? matchedVariant?.status ?? variant?.is_active ?? variant?.status
        ),
        image_id: this.extractVariantImageId({
          image_id: matchedVariant?.image_id ?? variant?.image_id,
          media_id: matchedVariant?.media_id ?? variant?.media_id,
          imageId: matchedVariant?.imageId ?? variant?.imageId,
          mediaId: matchedVariant?.mediaId ?? variant?.mediaId,
          image: matchedVariant?.image ?? variant?.image,
          media: matchedVariant?.media ?? variant?.media
        }),
        image_preview_url:
          this.resolveVariantImagePreviewUrl(variant) ||
          this.resolveVariantImagePreviewUrl(matchedVariant),
        attributes_detail:
          Array.isArray(variant?.attributes_detail) && variant.attributes_detail.length > 0
            ? variant.attributes_detail
            : Array.isArray(matchedVariant?.attributes_detail)
              ? matchedVariant.attributes_detail
              : []
      };

      return {
        ...mergedVariant,
        variant_name: this.resolveVariantName(mergedVariant)
      };
    });
  }

  getVariantDisplayName(variantCtrl: any, index: number): string {
    if (!variantCtrl) {
      return `Variant ${index + 1}`;
    }

    const row = {
      variant_name: variantCtrl.get?.('variant_name')?.value ?? '',
      sku_suffix: variantCtrl.get?.('sku_suffix')?.value ?? '',
      attribute_value_ids: variantCtrl.get?.('attribute_value_ids')?.value ?? []
    };

    const resolvedName = this.resolveVariantName(row);
    return resolvedName || `Variant ${index + 1}`;
  }

  private normalizeVariantCombinations(rows: any[]) {
    if (!Array.isArray(rows)) {
      return [];
    }

    return rows.map((row: any) => {
      const attributeValueIds = this.extractVariantAttributeValueIds(row);
      const skuSuffix = this.getVariantSkuSuffix(row);
      const normalizedRow = {
        sku_suffix: skuSuffix,
        variant_name: row?.variant_name || '',
        attribute_value_ids: attributeValueIds,
        attributes_detail: Array.isArray(row?.attributes_detail) ? row.attributes_detail : [],
        price: row?.price ?? '',
        stock: row?.stock ?? 0,
        is_active: this.normalizeVariantStatus(row?.is_active ?? row?.status ?? row?.isActive),
        image_id: this.extractVariantImageId(row),
        image_preview_url: this.resolveVariantImagePreviewUrl(row)
      };

      return {
        ...normalizedRow,
        variant_name: this.resolveVariantName(normalizedRow)
      };
    });
  }

  private syncSelectedAttributesFromExistingVariants(variantsSource: any[] = this.variableCombinations) {
    if (!Array.isArray(this.allAttributes) || this.allAttributes.length === 0) {
      return;
    }

    const selectedValueIds = new Set<string>();
    (variantsSource || []).forEach((variant: any) => {
      const ids = Array.isArray(variant?.attribute_value_ids) ? variant.attribute_value_ids : [];
      ids.forEach((id: any) => selectedValueIds.add(String(id)));
    });

    if (selectedValueIds.size === 0) {
      this.selectedAttributesForVariations = [];
      return;
    }

    this.selectedAttributesForVariations = this.allAttributes
      .map((attr: any) => {
        const values = Array.isArray(attr?.values) ? attr.values : [];
        const selectedIds = values
          .filter((value: any) => selectedValueIds.has(String(value.id)))
          .map((value: any) => value.id);

        if (selectedIds.length === 0) {
          return null;
        }

        return this.buildSelectedVariationAttribute(attr, { value_ids: Array.from(new Set(selectedIds)) });
      })
      .filter((attr: any) => attr !== null);
  }

  private mapAttributesFromVariants(variants: any[]) {
    this.syncSelectedAttributesFromExistingVariants(variants);
  }

  private applyVariantsFromSource(variants: any[]) {
    const normalizedVariants = this.normalizeVariantCombinations(variants || []);
    const currentSnapshot = this.getCurrentVariantsSnapshot();
    const mergeCandidates = [...this.variantsMergeCache, ...currentSnapshot];
    const mergedVariants = this.mergeVariantsWithExistingData(normalizedVariants, mergeCandidates);

    this.variableCombinations = mergedVariants;
    this.rebuildVariantsFormArray(mergedVariants);
    this.mapAttributesFromVariants(mergedVariants);
    this.variantsMergeCache = mergedVariants;
  }

  private loadVariantsForEditClone() {
    const localVariants = this.extractVariantsFromItem(this.data?.item);
    if (localVariants.length > 0) {
      this.productType = 'variable';
      this.applyVariantsFromSource(localVariants);
      this.tryAutoGeneratePreviewCombinationsForUpdate();
      return;
    }

    const shouldFetchFromApi =
      (this.data?.mode === 'update' || this.data?.mode === 'clone') &&
      !!this.data?.item?.id;
    if (!shouldFetchFromApi) {
      this.showVariantTable = false;
      return;
    }

    this.dataService
      .get(`products/${this.data.item.id}`)
      .pipe(
        catchError(() => of(null))
      )
      .subscribe((res: any) => {
        const productFromApi = res?.data?.data ?? res?.data ?? null;
        if (!productFromApi) {
          return;
        }

        const apiVariants = this.extractVariantsFromItem(productFromApi);
        if (apiVariants.length > 0) {
          this.productType = 'variable';
          this.applyVariantsFromSource(apiVariants);
          this.tryAutoGeneratePreviewCombinationsForUpdate();
          this.cd.detectChanges();
        }
      });
  }

  toggleAttributeSelection(attr: any) {
    const index = this.selectedAttributesForVariations.findIndex(
      (a) => String(a.attribute_id) === String(attr.id)
    );
    if (index > -1) {
      this.selectedAttributesForVariations.splice(index, 1);
    } else {
      this.selectedAttributesForVariations.push(this.buildSelectedVariationAttribute(attr));
    }
    this.clearGeneratedVariationsPreview();
  }

  isAttributeSelected(attrId: number | string) {
    return this.selectedAttributesForVariations.some(
      (a) => String(a.attribute_id) === String(attrId)
    );
  }

  toggleValueSelection(attrId: number | string, valueId: number | string) {
    const normalizedAttrId = String(attrId);
    const attr = this.selectedAttributesForVariations.find(
      (a) => String(a.attribute_id) === normalizedAttrId
    );

    if (!attr) {
      return;
    }

    const normalizedValueId = String(valueId);
    const vIndex = attr.value_ids.findIndex((id: any) => String(id) === normalizedValueId);
    if (vIndex > -1) {
      attr.value_ids.splice(vIndex, 1);
    } else {
      attr.value_ids.push(valueId);
    }
    this.clearGeneratedVariationsPreview();
  }

  isValueSelected(attrId: number | string, valueId: number | string) {
    const normalizedAttrId = String(attrId);
    const normalizedValueId = String(valueId);
    const attr = this.selectedAttributesForVariations.find(
      (a) => String(a.attribute_id) === normalizedAttrId
    );
    return attr ? attr.value_ids.some((id: any) => String(id) === normalizedValueId) : false;
  }

  hasSelectedVariationValues() {
    if (this.selectedAttributesForVariations.length === 0) {
      return false;
    }
    return this.getAttributesMissingValueSelection().length === 0;
  }

  hasIncompleteAttributeSelections() {
    return (
      this.selectedAttributesForVariations.length > 0 &&
      this.getAttributesMissingValueSelection().length > 0
    );
  }

  getIncompleteAttributeNames() {
    return this.getAttributesMissingValueSelection()
      .map((attr: any) => attr?.name)
      .filter((name: any) => typeof name === 'string' && name.trim() !== '')
      .join(', ');
  }

  private getAttributesMissingValueSelection() {
    return this.selectedAttributesForVariations.filter(
      (attr: any) => !Array.isArray(attr?.value_ids) || attr.value_ids.length === 0
    );
  }

  private clearGeneratedVariationsPreview() {
    this.variantsMergeCache = this.getCurrentVariantsSnapshot();
    if (this.variableCombinations.length > 0) {
      this.variableCombinations = [];
    }
    if (this.variantsReactiveForm) {
      this.rebuildVariantsFormArray([]);
    }
    this.showVariantTable = false;
  }

  private shouldAutoGeneratePreviewCombinations(): boolean {
    const isUpdateMode = this.data?.mode === 'update';
    if (!isUpdateMode) {
      return false;
    }

    if (String(this.productType || '').toLowerCase() !== 'variable') {
      return false;
    }

    return Array.isArray(this.allAttributes) && this.allAttributes.length > 0;
  }

  private tryAutoGeneratePreviewCombinationsForUpdate() {
    if (!this.shouldAutoGeneratePreviewCombinations()) {
      return;
    }

    if (this.hasAutoPreviewedUpdateCombinations || this.isAutoPreviewInProgress) {
      return;
    }

    if (!this.hasSelectedVariationValues()) {
      return;
    }

    this.isAutoPreviewInProgress = true;
    void this.generateVariationsWithOptions({ silent: true })
      .then((generated) => {
        if (generated) {
          this.hasAutoPreviewedUpdateCombinations = true;
        }
      })
      .finally(() => {
        this.isAutoPreviewInProgress = false;
      });
  }

  private async generateVariationsWithOptions(options: { silent?: boolean } = {}): Promise<boolean> {
    const silent = !!options.silent;

    if (this.selectedAttributesForVariations.length === 0) {
      if (!silent) {
        this.globalService.showToast({
          success: false,
          message: 'Please select at least one attribute to preview combinations'
        });
      }
      return false;
    }

    const missingSelections = this.getAttributesMissingValueSelection();
    if (missingSelections.length > 0) {
      if (!silent) {
        const names = this.getIncompleteAttributeNames();
        this.globalService.showToast({
          success: false,
          message: names
            ? `Please select at least one value for: ${names}`
            : 'Please select values for all selected attributes'
        });
      }
      return false;
    }

    const attributeValues = this.selectedAttributesForVariations
      .map((attr: any) => {
        const uniqueValues = new Map<string, any>();
        (attr?.value_ids || []).forEach((id: any) => {
          const key = String(id ?? '').trim();
          if (key !== '' && !uniqueValues.has(key)) {
            uniqueValues.set(key, id);
          }
        });
        return Array.from(uniqueValues.values());
      })
      .filter((ids: any[]) => ids.length > 0);

    if (attributeValues.length === 0) {
      if (!silent) {
        this.globalService.showToast({
          success: false,
          message: 'Please select at least one value to preview combinations'
        });
      }
      return false;
    }

    const payload = {
      attribute_values: attributeValues
    };

    const res: any = await firstValueFrom(
      this.dataService.post(payload, 'attributes/generate-preview').pipe(
        catchError(() => of(null))
      )
    );

    if (!res?.success) {
      if (!silent) {
        this.globalService.showToast({
          success: false,
          message: res?.message || 'Failed to generate variant combinations'
        });
      }
      return false;
    }

    const previewRows = Array.isArray(res?.data)
      ? res.data
      : Array.isArray(res?.data?.data)
        ? res.data.data
        : Array.isArray(res?.combinations)
          ? res.combinations
          : [];

    if (previewRows.length === 0) {
      if (!silent) {
        this.globalService.showToast({
          success: false,
          message: 'No combinations generated for selected attributes'
        });
      }
      return false;
    }

    this.applyVariantsFromSource(previewRows);
    if (!silent) {
      this.globalService.showToast({ success: true, message: 'Variants generated' });
    }
    this.cd.detectChanges();
    return true;
  }

  generateVariations() {
    void this.generateVariationsWithOptions();
  }

  openVariantGenerator(modalTemplate: any) {
    this.modalService.open(modalTemplate, {
      size: 'xl',
      centered: true,
      scrollable: true,
      backdrop: 'static'
    });
  }

  ngOnInit() {
    this.domain = window.location.origin;
    this.hasAutoPreviewedUpdateCombinations = false;
    this.isAutoPreviewInProgress = false;
    this.getCategoryList();
    this.loadAllAttributes();
    this.initializeForms();

    this.productDetails.valueChanges.subscribe(() => {
      this.cd.detectChanges();
    });

    if (this.data?.item) {
        this.productType = this.data.item.product_type || 'simple';
        this.loadVariantsForEditClone();
        const details = this.data.item.product_details;
        if (details) {
            const isElementor = (val: string) => val?.includes('<!-- ELEMENTOR_BLOCKS:') || val?.includes('elementor-content-wrapper');

            if (isElementor(details.shortDescription)) this.elementorMode.shortDescription = true;
            if (isElementor(details.productDescription)) this.elementorMode.productDescription = true;
            if (isElementor(details.features)) this.elementorMode.features = true;
        }
    }

  }

  removeVariantAt(index: number) {
    if (!this.variantsReactiveForm || index < 0 || index >= this.variantsFormArray.length) {
      return;
    }
    const variantGroup = this.variantsFormArray.at(index) as FormGroup;
    const variantKey = this.getVariantUploadKey(variantGroup?.getRawValue?.() ?? {}, index);
    this.pendingVariantImageFiles.delete(variantKey);

    this.variantsFormArray.removeAt(index);
    if (Array.isArray(this.variableCombinations) && this.variableCombinations.length > index) {
      this.variableCombinations.splice(index, 1);
    }
    this.variantsMergeCache = this.getCurrentVariantsSnapshot();
    this.showVariantTable = this.variantsFormArray.length > 0;
  }

  variantHasColorAttribute(variantCtrlOrRow: any): boolean {
    if (this.colorAttributeValueIds.size === 0 || !variantCtrlOrRow) {
      return false;
    }

    const rawAttributeValueIds = variantCtrlOrRow?.get
      ? variantCtrlOrRow.get('attribute_value_ids')?.value
      : variantCtrlOrRow?.attribute_value_ids;
    const attributeValueIds = this.normalizeAttributeValueIds(rawAttributeValueIds);

    return attributeValueIds.some((id: any) => this.colorAttributeValueIds.has(String(id)));
  }

  // shouldShowVariantImageColumn(): boolean {
  //   if (!this.showVariantTable || !this.variantsReactiveForm || this.colorAttributeValueIds.size === 0) {
  //     return false;
  //   }

  //   return this.variantsFormArray.controls.some((variantCtrl: any) => this.variantHasColorAttribute(variantCtrl));
  // }

  isVariantImageUploading(index: number): boolean {
    return this.uploadingVariantImageIndexes.has(index);
  }

  private getVariantUploadKey(variantRow: any, index?: number): string {
    const normalizedAttributeIds = this
      .normalizeAttributeValueIds(variantRow?.attribute_value_ids)
      .map((id: any) => String(id).trim())
      .filter((id: string) => id !== '')
      .sort();

    if (normalizedAttributeIds.length > 0) {
      return `attr:${normalizedAttributeIds.join('|')}`;
    }

    const skuSuffix = String(variantRow?.sku_suffix ?? '').trim().toLowerCase();
    if (skuSuffix !== '') {
      return `sku:${skuSuffix}`;
    }

    return `idx:${index ?? -1}`;
  }

  private async uploadPendingVariantImages(moduleId: number | string): Promise<{ hadPending: boolean; success: boolean }> {
    if (!this.variantsReactiveForm || this.variantsFormArray.length === 0) {
      return { hadPending: false, success: true };
    }

    const hadPending = this.pendingVariantImageFiles.size > 0;
    if (!hadPending) {
      return { hadPending: false, success: true };
    }

    let uploadedCount = 0;
    let failedCount = 0;

    for (let index = 0; index < this.variantsFormArray.length; index++) {
      const variantGroup = this.variantsFormArray.at(index) as FormGroup;
      const variantKey = this.getVariantUploadKey(variantGroup.getRawValue(), index);
      const pendingFile = this.pendingVariantImageFiles.get(variantKey);
      if (!pendingFile) {
        continue;
      }

      try {
        this.uploadingVariantImageIndexes.add(index);
        this.cd.detectChanges();

        const currentPreviewUrl = String(variantGroup.get('image_preview_url')?.value ?? '');
        const uploadResult = await this.uploadVariantImageFile(pendingFile, moduleId);
        variantGroup.patchValue({
          image_id: uploadResult.imageId,
          image_preview_url: uploadResult.imageUrl || currentPreviewUrl
        });
        variantGroup.markAsDirty();

        this.pendingVariantImageFiles.delete(variantKey);
        uploadedCount++;
      } catch (error) {
        failedCount++;
      } finally {
        this.uploadingVariantImageIndexes.delete(index);
        this.cd.detectChanges();
      }
    }

    if (uploadedCount > 0) {
      this.variantsReactiveForm.markAsDirty();
      const currentSnapshot = this.getCurrentVariantsSnapshot();
      this.variableCombinations = currentSnapshot;
      this.variantsMergeCache = currentSnapshot;
    }

    if (failedCount > 0) {
      this.globalService.showToast({
        success: false,
        message: `Failed to upload ${failedCount} variant image${failedCount > 1 ? 's' : ''}`
      });
      return { hadPending, success: false };
    }

    return { hadPending, success: true };
  }

  private async syncVariantsForProduct(productId: number | string): Promise<boolean> {
    const variantsResult = this.getVariantsPayloadForPublish();
    if (variantsResult.hasError) {
      return false;
    }

    if (variantsResult.variants.length === 0) {
      return true;
    }

    try {
      const response: any = await firstValueFrom(
        this.dataService.put(
          {
            product_type: this.productType,
            variants: variantsResult.variants
          },
          `products/${productId}`
        )
      );

      if (response?.error || response?.success === false) {
        this.globalService.showToast({
          success: false,
          message: response?.message || 'Product saved, but variant images were not linked'
        });
        return false;
      }

      return true;
    } catch (error) {
      this.globalService.showToast({
        success: false,
        message: 'Product saved, but variant images were not linked'
      });
      return false;
    }
  }

  async onVariantImageSelected(event: Event, index: number) {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0];

    if (!file || !this.variantsReactiveForm || index < 0 || index >= this.variantsFormArray.length) {
      if (input) {
        input.value = '';
      }
      return;
    }

    const variantGroup = this.variantsFormArray.at(index) as FormGroup;
    const previousPreviewUrl = String(variantGroup.get('image_preview_url')?.value ?? '');

    try {
      const localPreviewUrl = await this.readFileAsDataUrl(file);

      variantGroup.patchValue({
        image_preview_url: localPreviewUrl
      });
      variantGroup.markAsDirty();
      this.variantsReactiveForm.markAsDirty();

      const variantKey = this.getVariantUploadKey(variantGroup.getRawValue(), index);
      this.pendingVariantImageFiles.set(variantKey, file);

      this.globalService.showToast({
        success: true,
        message: 'Variant image selected. It will upload when you save the product'
      });
    } catch (error: any) {
      variantGroup.patchValue({
        image_preview_url: previousPreviewUrl
      });

      this.globalService.showToast({
        success: false,
        message: error?.message || 'Failed to prepare variant image'
      });
    } finally {
      const currentSnapshot = this.getCurrentVariantsSnapshot();
      this.variableCombinations = currentSnapshot;
      this.variantsMergeCache = currentSnapshot;

      if (input) {
        input.value = '';
      }
      this.cd.detectChanges();
    }
  }

  isNotEmpty(obj: any): boolean {
    return obj && Object.keys(obj).length > 0;
  }
  initializeForms() {
    // console.log('data',this.data);
    // console.log('this.data.category.length ===>',this.data.images );
    console.log('log==>', this.data?.item);
    this.initializeVariantsReactiveForm();
    //    Object.keys(this.data.flags || {}).length ||
    // Object.keys(this.data.images || {}).length ||

    this.permaLink = this.data?.item?.product_details?.permaLink;
    const hasValidData =
      Object.keys(this.data?.item?.product_details || {}).length;
    if (this.data?.item && hasValidData) {
      this.onGetId(this.data?.item?.category.id);
      this.finalpriceObj = this.data?.item?.price_data;
      // for (let i = 0; i < this.data.item.images.length; i++) {
      //   const element = this.data.item.images[i];
      //   if (element.type == "gallery") {
      //     this.selectedFile.push(element);
      //   }
      //   else{
      //     this.selectedThumbImg = element;
      //   }
      // }
      console.log('this.thumbGallery==>',this.thumbGallery);
      console.log('this.thumbPreview==>',this.thumbPreview);
      
      
      console.log('log==> enrer', this.data?.item);
      this.isUpdateproduct = true;
      if (this.data?.item?.flags) {
        console.log('this.data.category.length ===>', this.data?.item.flags);

        this.productStatus = this.data?.item.flags;
      }
      if (this.data?.item?.images) {
        const images = this.data?.item?.images || [];

        this.removedGalleryMediaIds.clear();
        this.removedGalleryImageUrls.clear();
        this.initializeGalleryEntries(images);

        const previewList = images
          .filter((img: any) => {
            const type = String(img?.type || '').toLowerCase();
            return type === 'thumbnail' || type === 'thumbpreview';
          })
          .map((img: any) => img.url);

        this.thumbPreview = previewList?.[0] || '';
        console.log('thumbPreview===>', this.thumbGallery);

        // this.thumbPreview =  environment.DOMAIN + '/' + this.data?.thumbnail;
        // console.log('thumbPreview==>',this.thumbPreview);

        // this.cd.detectChanges();
      }
      else {
        this.removedGalleryMediaIds.clear();
        this.removedGalleryImageUrls.clear();
        this.initializeGalleryEntries([]);
      }
      if (this.data?.item?.category) {
        console.log(' this.data?.category==>', Array.isArray(this.data?.item?.category));

        // category exists and is NOT blank
        // this.getCategoryList();
        this.addProductDetails();
        this.productOptionType();
        this.submitProductMultipleOptionForm();
        // this.addCategoriesForm();
        this.addTagsForm();
        this.mediaForm();
        this.inventryForm();
        this.priceForm();
        this.shippingForm();
        this.productAttributeForm();
        this.shippingConfigForms();
        this.offerFormGroup();
        this.seoFormGroup();
      }
    }
    else {
      this.removedGalleryMediaIds.clear();
      this.removedGalleryImageUrls.clear();
      this.initializeGalleryEntries([]);
      this.addProductDetails();
      this.productOptionType();
      this.submitProductMultipleOptionForm();
      // this.addCategoriesForm();
      this.addTagsForm();
      this.mediaForm();
      this.inventryForm();
      this.priceForm();
      this.shippingForm();
      this.productAttributeForm();
      this.shippingConfigForms();
      this.offerFormGroup();
      this.seoFormGroup();
    }
    // this.addInverntoryForm();
  }
  closeModal() {
    console.log('this.hasUnsavedChanges()==>', this.hasUnsavedChanges());

    if (this.hasUnsavedChanges()) {
      const confirmLeave = confirm(
        'You have unsaved changes. Do you really want to continue?'
      );

      if (!confirmLeave) {
        return;
      }
      else {
        this.activeModal.close();
        this.isUpdateproduct = true;
      }
    }
    else {
      this.activeModal.close();
      this.isUpdateproduct = true;
    }
  }
  addProductDetails() {
    this.productDetails = this.fb.group({
      productTitle: [this.data?.item?.title, Validators.required], //product_title
      shortDescription: [this.data?.item?.product_details?.shortDescription], //short_description
      productDescription: [this.data?.item?.product_details?.productDescription], //description
      features: [this.data?.item?.product_details?.features], //features
      permaLink: [this.data?.item?.product_details?.permaLink],
      // productStatus: [this.data?.item.attributes?.productDetailsObj?.productStatus],
      productDescriptionImageGallery: [this.data?.item?.product_details?.productDescriptionImageGallery]
    });
  }
  get addProductDetailsValidation() {
    return this.productDetails?.controls;
  }

  //   getStatusKeys(): string[] {
  //   return Object.keys(this.productStatus);
  // }

  onStatusChange(index: any, event: any) {
    this.productStatus[index].isActive = event.target.checked;

    console.log('Updated productStatus =>', this.productStatus);
  }


  // if (event.target.checked) {
  //   this.productStatusArray.push(new FormControl(value));
  // } else {
  //   const index = this.productStatusArray.controls
  //     .findIndex(x => x.value === value);

  //   this.productStatusArray.removeAt(index);
  // }
  productOptionType() {
    this.productOptionData = this.fb.group({
      generalProductData: [false],
      downloadableProductData: [false],
    });
  }

  // onStatusChange(event: any) {
  //   const selectedOptions = Array.from(event.target.selectedOptions).map(
  //     (option: any) => option.value
  //   );

  //   this.productDetails.patchValue({
  //     productStatus: selectedOptions,
  //   });
  // }

  mediaForm() {
    this.productMediaSection = this.fb.group({
      thumbUpload: ['', Validators.required],
      galleryUpload: []
    });
  }

  inventryForm() {
    this.productInventrySection = this.fb.group({
      sku: [this.data?.item?.sku, Validators.required],
      manageStock: [this.data?.item?.inventory?.manageStock],
      stockStatus: [this.data?.item?.inventory?.stockStatus],
      soldIndividually: [this.data?.item?.inventory?.soldIndividually],
      productCode: [this.data?.item?.inventory?.productCode],
      lowStockWarning: [this.data?.item?.inventory?.lowStockWarning],
      showStockQuantity: [this.data?.item?.inventory?.showStockQuantity],
      showStockWithText: [this.data?.item?.inventory?.showStockWithText],
      hideStock: [this.data?.item?.inventory?.hideStock],
    });
  }

  priceForm() {
    this.priceSection = this.fb.group({
      regularPrice: [this.data?.item?.price_data?.regularPrice, [Validators.required, Validators.min(1)]],
      salePrice: [this.data?.item?.price_data?.salePrice, [Validators.min(0)]],
      gstPercent: [this.data?.item?.price_data?.gstPercent],
      discountType: ['Flat'],
      priceDateStart: [''],
      priceDateEnd: [''],
    });

    // Subscribe to discount type changes to update validators
    this.priceSection.get('discountType')?.valueChanges.subscribe(type => {
      this.updateDiscountValidators(type);
    });
  }
  getGstDiscount() {
    this.finalpriceObj = this.priceSection.value;
    this.finalpriceObj.lastUpdatedDate = new Date().getTime();
    let finalPayload: any = {};
    // console.log('priceObj==>',finalpriceObj);

    if (this.finalpriceObj.regularPrice >= this.finalpriceObj.salePrice) {
      finalPayload.salePrice = this.finalpriceObj.salePrice;
    }
    else {
      finalPayload.salePrice = this.finalpriceObj.regularPrice;
    }
    finalPayload.quantity = 1;
    finalPayload.gstPercent = this.priceSection.value.gstPercent;

    // let payLoad = 
    this.dataService
      .postCommonApi(finalPayload, 'calculate-prices')
      .pipe(
        catchError((err) => {
          console.error('Error:', err);
          setTimeout(() => {
            // this.globalService.showToast(err);
          }, 100);
          return of(err);
        })
      )
      .subscribe((res: any) => {
        console.log('res==>', res);
        if (res.success) {
          this.finalpriceObj.caclulatedObj = res.data;
          this.cd.detectChanges();
        }
      })
  }
  shippingForm() {
    this.shippingInfoSection = this.fb.group({
      weight: [this.data?.item?.shipping_info?.weight, Validators.min(0)],
      length: [this.data?.item?.shipping_info?.length, Validators.min(0)],
      width: [this.data?.item?.shipping_info?.width, Validators.min(0)],
      height: [this.data?.item?.shipping_info?.height, Validators.min(0)],
      shippingClass: ['0'],
    });
  }
  productAttributeForm() {
    this.productAttributesForm = this.fb.group({
      selectedAttribute: [''],
      attributes: this.fb.array([]),
    });
  }
  get attributes(): FormArray {
    return this.productAttributesForm.get('attributes') as FormArray;
  }
  addAttribute() {
    const attr = this.fb.group({
      name: [''], // attribute name
      value: [''], // attribute value
    });

    this.attributes.push(attr);
    //console.log('this.attributes==>', this.attributes);
  }

  shippingConfigForms() {
    this.shippingConfigForm = this.fb.group({
      estimateShippingTime: [this.data?.item?.shipping_config?.estimateShippingTime],
      freeShipping: [this.data?.item?.shipping_config?.freeShipping],
      flatRate: [this.data?.item?.shipping_config?.flatRate],
      quantityMulitiply: [this.data?.item?.shipping_config?.quantityMulitiply],
      cashOnDelivery: [this.data?.item?.shipping_config?.cashOnDelivery],
    });
  }

  offerFormGroup() {
    this.offerForm = this.fb.group({
      flashDeal: [this.data?.item?.offer?.flashDeal],
      todaysDeal: [this.data?.item?.offer?.todaysDeal],
      featured: [this.data?.item?.offer?.featured],
    });
  }
  seoFormGroup() {
    this.seoForm = this.fb.group({
      focusKeyphrase: [this.data?.item?.seo?.focusKeyphrase, Validators.required],
      metaTitle: [this.data?.item?.seo?.metaTitle, [Validators.required, Validators.maxLength(60)]],
      slugText: [this.data?.item?.seo?.slugText],
      metaDscr: [this.data?.item?.seo?.metaDscr, [Validators.required, Validators.maxLength(160)]],
    });
  }

  submitProductMultipleOptionForm() {
    this.productMultipleOptionForm = this.fb.group({
      // Media Tab
      // thumbUpload: ['', Validators.required],
      // galleryUpload: [''],

      // Inventory Tab
      // sku: ['', Validators.required],
      // manageStock: [false],
      // stockStatus: [],
      // soldIndividually: [false],
      // productCode: [''],
      // lowStockWarning: [0],
      // showStockQuantity: [false],
      // showStockWithText: [false],
      // hideStock: [false],

      // Price Tab
      // regularPrice: [0, [Validators.required, Validators.min(0)]],
      // salePrice: [0, Validators.min(0)],
      // discountType: ['1'],
      // priceDateStart: [''],
      // priceDateEnd: [''],

      //attribute

      // Shipping Info Tab
      // weight: [0, Validators.min(0)],
      // length: [0, Validators.min(0)],
      // width: [0, Validators.min(0)],
      // height: [0, Validators.min(0)],
      // shippingClass: ['0'],

      // Shipping COnfiguration
      // estimateShippingTime: [''],
      // freeShipping: [false],
      // flatRate: [false],
      // quantityMulitiply: [false],
      // cashOnDelivery: [false],

      // offer
      // flashDeal: [false],
      // todaysDeal: [false],
      // featured: [false],

      // SEO Data
      // focusKeyphrase: ['', Validators.required],
      // metaTitle: ['', [Validators.required, Validators.maxLength(60)]],
      // slugText: ['', [Validators.required, Validators.pattern('^[a-z0-9-]+$')]],
      // metaDscr: ['', [Validators.required, Validators.maxLength(160)]],

      // Published Panel
      status: ['1', Validators.required],
      visibility: ['1', Validators.required],
      publishDate: ['', Validators.required],

      // Category Panel
      // categorySearch: [''],
      // newCategoryName: ['', Validators.required],
      // newCategoryParent: ['']
    });
  }

  // addCategoriesForm() {
  //   this.categoryForm = this.fb.group({
  //     search: [''],
  //     selectedCategories: this.fb.array([]),
  //     newCategory: this.fb.group({
  //       name: [''],
  //       parent: [''],
  //     }),
  //   });
  // }

  addTagsForm() {
    this.tagsForm = this.fb.group({
      tagInput: [''],
      tags: [this.data?.item?.tags?.tags],
    });
  }
  // private initializeCategoryControls(): void {
  //   const selectedCategories = this.categoryForm.get('selectedCategories') as FormArray;
  //   selectedCategories.clear();

  //   this.categories.forEach((category) => {
  //     selectedCategories.push(new FormControl(false));
  //     if (category.subCategories) {
  //       category.subCategories.forEach((sub) => {
  //         selectedCategories.push(new FormControl(false));
  //         if (sub.subCategories) {
  //           sub.subCategories.forEach(() => {
  //             selectedCategories.push(new FormControl(false));
  //           });
  //         }
  //       });
  //     }
  //   });
  // }

  // get selectedCategories(): FormArray {
  //   return this.categoryForm.get('selectedCategories') as FormArray;
  // }

  get newCategoryForm(): FormGroup {
    return this.categoryForm.get('newCategory') as FormGroup;
  }

  onSearch(): void {
    //console.log('Search Query:', this.categoryForm.get('search')?.value);
    // Add search logic here
  }
  getCategoryLabel(index: number): string {
    // Simplified way to show category name from index
    const flatList: string[] = [];
    this.categories.forEach((c) => {
      flatList.push(c.name);
      c.subCategories?.forEach((sub) => {
        flatList.push('— ' + sub.name);
        sub.subCategories?.forEach((subSub) => flatList.push('—— ' + subSub.name));
      });
    });
    return flatList[index] || 'Category ' + index;
  }
  addNewCategory(): void {
    const newCat = this.newCategoryForm.value;
    //console.log('New Category:', newCat);
  }

  // Tags start

  addTag() {
    //console.log('this.tagsForm.value==>', this.tagsForm.value);

    const tagValue = this.tagsForm.value.tagInput?.trim();
    //console.log('tagValue ==>', tagValue);

    if (tagValue) {
      const currentTags = this.tagsForm.value.tags || [];
      const newTags = [
        ...currentTags,
        ...tagValue
          .split(',')
          .map((t: any) => t.trim())
          .filter(Boolean),
      ];
      this.tagsForm.patchValue({ tags: newTags, tagInput: '' });
    }
  }

  // ✅ Remove tag by index
  removeTag(index: number) {
    const currentTags = [...this.tagsForm.value.tags];
    currentTags.splice(index, 1);
    this.tagsForm.patchValue({ tags: currentTags });
  }

  // ✅ Select from most used tags
  selectTag(tag: string) {
    const currentTags = this.tagsForm.value.tags || [];
    if (!currentTags.includes(tag)) {
      this.tagsForm.patchValue({ tags: [...currentTags, tag] });
    }
  }
  onThumbSelect(event: any) {
    const file = event.target.files[0];
    this.thumbFile = file;

    // Create image preview
    const reader = new FileReader();
    reader.onload = () => {
      this.thumbPreview.push(reader.result as string);
    };
    reader.readAsDataURL(file);
  }

  // media upload photo fnc
  onFileSelect(event: any) {
    const files = event?.target?.files;
    if (!files?.length) {
      return;
    }

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!file) {
        continue;
      }

      const entry: ProductGalleryImageEntry = {
        source: 'new',
        url: '',
        mediaId: null,
        file
      };
      this.galleryImageEntries.push(entry);
      this.selectedFile.push(file);

      const reader = new FileReader();
      reader.onload = () => {
        entry.url = String(reader.result ?? '');
        this.syncThumbGalleryFromEntries();
        this.cd.detectChanges();
      };
      reader.onerror = () => {
        this.removeSelectedFileByReference(file);
        const entryIndex = this.galleryImageEntries.indexOf(entry);
        if (entryIndex > -1) {
          this.galleryImageEntries.splice(entryIndex, 1);
        }
        this.syncThumbGalleryFromEntries();
        this.rebuildGalleryInputFileList();
        this.cd.detectChanges();
      };
      reader.readAsDataURL(file);
    }

    this.rebuildGalleryInputFileList();
    this.productMediaSection?.markAsDirty();
    this.cd.detectChanges();
  }
  onFileProductDescriptionGallery(event: any) {
    // const file = event.target.files[0];
    for (let i = 0; i < event.target.files.length; i++) {
      const file = event.target.files[i];
      if (!file) return;
      this.selectProductDesciptionImageGallery.push(file);
      //console.log('this.selectedFile===>', this.selectedFile);

      // Preview if needed
      const reader = new FileReader();
      reader.onload = () => {
        // this.thumbPreview = reader.result as string;
        this.prodDescriptionImageGallery.push(reader.result as string);
        this.cd.detectChanges();
      };

      const element = this.selectProductDesciptionImageGallery[i];
      reader.readAsDataURL(element);
      this.cd.detectChanges();

    }
    // Call upload immediately
    // this.uploadImage();
    //console.log(' this.thumbGallery===>', this.thumbGallery);
  }
  onFileSelectThumb(event: any) {
    const file = event.target.files[0];
    if (!file) return;
    // const file = event.target.files[0];
    // this.selectedFile.push(file);
    this.selectedThumbImg = file;

    // Preview if needed
    const reader = new FileReader();
    reader.onload = () => {
      // this.thumbPreview = reader.result as string;
      this.thumbPreview = reader.result as string;
      this.cd.detectChanges();
    };
    reader.readAsDataURL(file);
    // Call upload immediately
    // this.uploadImage();
  }
  uploadImage() {
    if (!this.selectedThumbImg) return;
    this.buildMediaUploadFormData(this.selectedThumbImg, 'gallery');
  }

  onGallerySelect(event: any) {
    this.galleryFiles = event.target.files;
  }

  private getPrimaryThumbPreviewUrl(): string {
    if (Array.isArray(this.thumbPreview)) {
      return this.thumbPreview.find((url: any) => typeof url === 'string' && url.trim() !== '') || '';
    }
    return typeof this.thumbPreview === 'string' ? this.thumbPreview : '';
  }

  private getGalleryPreviewUrls(): string[] {
    if (!Array.isArray(this.galleryImageEntries) || this.galleryImageEntries.length === 0) {
      return [];
    }
    return this.galleryImageEntries
      .map((entry: ProductGalleryImageEntry) => entry.url)
      .filter((url: any) => typeof url === 'string' && url.trim() !== '');
  }

  private isRemoteLikeImageUrl(url: string): boolean {
    if (!url) return false;
    const lowered = url.toLowerCase();
    return !lowered.startsWith('data:') && !lowered.startsWith('blob:');
  }

  private buildAbsoluteImageUrl(url: string): string {
    if (/^https?:\/\//i.test(url)) return url;
    if (url.startsWith('/')) {
      return `${environment.DOMAIN.replace(/\/$/, '')}${url}`;
    }
    return `${environment.DOMAIN.replace(/\/$/, '')}/${url.replace(/^\/+/, '')}`;
  }

  private getFileNameFromUrl(url: string, fallbackBase: string): string {
    const cleanedUrl = url.split('?')[0];
    const rawName = cleanedUrl.split('/').pop() || '';
    if (rawName.includes('.')) {
      return rawName;
    }
    return `${fallbackBase}.jpg`;
  }

  private async convertImageUrlToFile(url: string, fallbackBase: string): Promise<File | null> {
    try {
      const absoluteUrl = this.buildAbsoluteImageUrl(url);
      const response = await fetch(absoluteUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch image. Status: ${response.status}`);
      }
      const blob = await response.blob();
      const fileName = this.getFileNameFromUrl(absoluteUrl, fallbackBase);
      return new File([blob], fileName, { type: blob.type || 'image/jpeg' });
    } catch (error) {
      console.error('Failed to convert image URL to file:', url, error);
      return null;
    }
  }

  private uploadSingleMediaFile(file: File, moduleId: number | string, type: string) {
    const formData = this.buildMediaUploadFormData(file, type, moduleId);
    this.callUploadnediaSection(formData);
  }

  private async uploadProductMedia(moduleId: number | string) {
    if (this.selectedThumbImg instanceof File) {
      this.uploadSingleMediaFile(this.selectedThumbImg, moduleId, 'thumbnail');
    } else if (this.data?.mode === 'clone') {
      const thumbUrl = this.getPrimaryThumbPreviewUrl();
      if (this.isRemoteLikeImageUrl(thumbUrl)) {
        const thumbFile = await this.convertImageUrlToFile(thumbUrl, 'thumbnail-clone');
        if (thumbFile) {
          this.uploadSingleMediaFile(thumbFile, moduleId, 'thumbnail');
        }
      }
    }

    if (this.selectProductDesciptionImageGallery?.length) {
      for (const file of this.selectProductDesciptionImageGallery) {
        this.uploadSingleMediaFile(file, moduleId, 'photoDescriptionImageGallery');
      }
    }

    if (this.selectedFile?.length) {
      for (const file of this.selectedFile) {
        this.uploadSingleMediaFile(file, moduleId, 'gallery');
      }
    }

    if (this.data?.mode === 'clone') {
      const galleryUrls = Array.from(
        new Set(this.getGalleryPreviewUrls().filter((url) => this.isRemoteLikeImageUrl(url)))
      );

      for (let i = 0; i < galleryUrls.length; i++) {
        const galleryFile = await this.convertImageUrlToFile(galleryUrls[i], `gallery-clone-${i + 1}`);
        if (galleryFile) {
          this.uploadSingleMediaFile(galleryFile, moduleId, 'gallery');
        }
      }
    }
  }

  private getCustomAttributesPayload() {
    const rawAttributes = this.productAttributesForm?.get('attributes')?.value;
    if (!Array.isArray(rawAttributes)) {
      return [];
    }

    return rawAttributes
      .map((item: any) => ({
        name: String(item?.name ?? '').trim(),
        value: String(item?.value ?? '').trim()
      }))
      .filter((item: any) => item.name !== '' && item.value !== '');
  }

  private getVariantsPayloadForPublish() {
    const variantsSource =
      this.variantsReactiveForm && this.variantsFormArray.length > 0
        ? this.variantsFormArray.getRawValue()
        : Array.isArray(this.variableCombinations)
          ? this.variableCombinations
          : [];
    if (variantsSource.length === 0) {
      return { hasError: false, variants: [] as any[] };
    }

    const mappedVariants = [];

    for (let index = 0; index < variantsSource.length; index++) {
      const row = variantsSource[index] || {};

      const rawPrice = row.price;
      let normalizedPrice: number | null = null;
      if (rawPrice !== '' && rawPrice !== null && rawPrice !== undefined) {
        const parsedPrice = Number(rawPrice);
        if (Number.isNaN(parsedPrice) || parsedPrice < 0) {
          this.globalService.showToast({
            success: false,
            message: `Variant ${index + 1}: price must be a valid number`
          });
          return { hasError: true, variants: [] as any[] };
        }
        normalizedPrice = parsedPrice;
      }

      const parsedStock = Number(row.stock ?? 0);
      if (Number.isNaN(parsedStock) || parsedStock < 0) {
        this.globalService.showToast({
          success: false,
          message: `Variant ${index + 1}: stock must be 0 or greater`
        });
        return { hasError: true, variants: [] as any[] };
      }

      const normalizedImageId = this.extractVariantImageId(row);

      mappedVariants.push({
        sku_suffix: this.getVariantSkuSuffix(row),
        attribute_value_ids: this.extractVariantAttributeValueIds(row),
        price: normalizedPrice,
        stock: parsedStock,
        is_active: this.normalizeVariantStatus(row.is_active ?? row.status ?? row.isActive),
        ...(normalizedImageId !== null ? { image_id: normalizedImageId } : {})
      });
    }

    return { hasError: false, variants: mappedVariants };
  }

  async updateProduct() {
    console.log('this.data?.id===>', this.data?.item?.id);

    this.finalpriceObj = this.priceSection.value;
    this.finalpriceObj.lastUpdatedDate = new Date().getTime();

    // priceSection.priceDateStart = new Date(this.priceSection.value.priceDateStart).getTime();
    // priceSection.priceDateEnd = new Date(this.priceSection.value.priceDateEnd).getTime();
    const existingProductId = this.data?.item?.id;
    const mediaSectionPayload = this.buildMediaSectionPayload(existingProductId);
    if (existingProductId !== undefined && existingProductId !== null) {
      await this.uploadPendingVariantImages(existingProductId);
    }

    const variantsResult = this.getVariantsPayloadForPublish();
    if (variantsResult.hasError) {
      return;
    }
    const customAttributes = this.getCustomAttributesPayload();
    let finalData: any = {
      category_id: this.parentId,
      product_type: this.productType,
      ...(variantsResult.variants.length > 0 ? { variants: variantsResult.variants } : {}),
      media: mediaSectionPayload,
      title: this.productDetails.value.productTitle,
      description: this.productDetails.value.productDescription,
      shortDescription: this.productDetails.value.shortDescription,
      features: this.productDetails.value.features,
      inventory: this.productInventrySection.value,
      price_data: this.finalpriceObj,
      shipping_info: this.shippingInfoSection.value,
      shipping_config: this.shippingConfigForm.value,
      offer: this.offerForm.value,
      seo: this.seoForm.value,
      tags: this.tagsForm.value,
      flags: this.productStatus,
      product_details: this.productDetails.value,
      visibility: {
        status: this.productMultipleOptionForm.value.status,
        publishDate: this.productMultipleOptionForm.value.publishDate,
        visibility: this.productMultipleOptionForm.value.visibility,
      },
      attributes: customAttributes
    };
    console.log('this.permaLink==>', this.permaLink);

    finalData.product_details.permaLink = this.permaLink;
    let url = `products/${this.data?.item?.id}`
    this.dataService
      .put(finalData, url)
      .pipe(
        catchError((err) => {
          console.error('Error:', err);
          setTimeout(() => {
            // this.globalService.showToast(err);
          }, 100);
          return of(err);
        })
      )
      .subscribe(async (res: any) => {
        //console.log('Response:', res);
        if (res.error) {
          this.globalService.showToast(res.error);
          return;
        } else if (res.success == true) {
          let id = res.data.data.id;
          const removedGalleryResult = await this.syncRemovedGalleryMedia(id);
          if (removedGalleryResult.failedIds.length > 0) {
            console.warn('Some removed gallery images could not be synced by direct media delete endpoints', removedGalleryResult.failedIds);
          }

          this.uploadProductMedia(id).finally(() => {
            setTimeout(() => {
              this.globalService.showToast(res);
              this.activeModal.close('success');
            }, 100);
          });
        }
        // this.addCategory.reset();
        // this.imagePreview = '';
        // this.imageFile = null;
        // this.getCategoryList();
      });

  }
  confirmBeforeSaveProduct(action: any) {
    const confirmed = confirm(`Are you sure you want to ${action} this product?`);
    if (!confirmed) {
      return;
    }
    if (action == 'update') {
      void this.updateProduct();
    }
    else {
      void this.getProductDetails();
    }
  }
  async getProductDetails() {
    //console.log('productDetails==>', this.productDetails.value);

    let priceSection = this.priceSection.value;
    // priceSection.priceDateStart = new Date(this.priceSection.value.priceDateStart).getTime();
    // priceSection.priceDateEnd = new Date(this.priceSection.value.priceDateEnd).getTime();
    // media payload
    const mediaSectionPayload = this.buildMediaSectionPayload();
    const variantsResult = this.getVariantsPayloadForPublish();
    if (variantsResult.hasError) {
      return;
    }
    const customAttributes = this.getCustomAttributesPayload();
    let finalData: any = {
      category_id: this.parentId,
      product_type: this.productType,
      ...(variantsResult.variants.length > 0 ? { variants: variantsResult.variants } : {}),
      media: mediaSectionPayload,
      title: this.productDetails.value.productTitle,
      description: this.productDetails.value.productDescription,
      shortDescription: this.productDetails.value.shortDescription,
      features: this.productDetails.value.features,
      // productStatus:this.productDetails.value.productStatus,
      inventory: this.productInventrySection.value,
      price_data: this.finalpriceObj,
      shipping_info: this.shippingInfoSection.value,
      shipping_config: this.shippingConfigForm.value,
      offer: this.offerForm.value,
      seo: this.seoForm.value,
      tags: this.tagsForm.value,
      flags: this.productStatus,
      product_details: this.productDetails.value,
      // visibility:this.productMultipleOptionForm.value,
      visibility: {
        status: this.productMultipleOptionForm.value.status,
        publishDate: this.productMultipleOptionForm.value.publishDate,
        visibility: this.productMultipleOptionForm.value.visibility,
      },
      attributes: customAttributes
    };
    console.log('this.permaLink===>', this.permaLink);

    finalData.product_details.permaLink = this.permaLink;
    this.dataService
      .post(finalData, 'products')
      .pipe(
        catchError((err) => {
          console.error('Error:', err);
          setTimeout(() => {
            // this.globalService.showToast(err);
          }, 100);
          return of(err);
        })
      )
      .subscribe(async (res: any) => {
        //console.log('Response:', res);
        if (res.error) {
          this.globalService.showToast(res.error);
          return;
        } else if (res.success == true) {
          // let id = res.data.id;
          let id = res.data.data.id;

          const pendingVariantUploadResult = await this.uploadPendingVariantImages(id);
          if (pendingVariantUploadResult.hadPending) {
            await this.syncVariantsForProduct(id);
          }
          if (!pendingVariantUploadResult.success) {
            this.globalService.showToast({
              success: false,
              message: 'Product saved, but some variant images failed. Please edit and retry.'
            });
          }

          this.uploadProductMedia(id).finally(() => {
            setTimeout(() => {
              this.globalService.showToast(res);
              this.resetProductForm();
              this.activeModal.close('success');
            }, 100);
          });
        }
        // this.addCategory.reset();
        // this.imagePreview = '';
        // this.imageFile = null;
        // this.getCategoryList();
      });

    //   //console.log('thumbFile',this.thumbFile);  // FileList
    //   //console.log('galleryFiles',this.galleryFiles);  // FileList
    // //console.log('this.priceSection.value.priceDateStart==>',this.priceSection.value);
    // // price start and end time
    //   const priceDateStart = new Date(this.priceSection.value.priceDateStart).getTime();
    //   const priceDateEnd = new Date(this.priceSection.value.priceDateEnd).getTime();

    // media payload
    // let mediaSectionPayload = {
    // thumbFile:this.thumbFile,
    // galleryFiles:this.galleryFiles
    // }

    //   //console.log('productDetails==>',this.productDetails.value);
    //   //console.log('productOptionData==>',this.productOptionData.value);
    // //console.log('submitProductMultipleOptionForm==>',this.productMultipleOptionForm.value);
    // // //console.log('New Category:', this.categoryForm.get('newCategory')?.value);
    // //console.log('New Category:', this.newCategoryForm.value);
    // //console.log('this.tagsForm==>',this.tagsForm.value.tags);
    // //console.log('selectedCategories==>',this.selectedCategories.value);

    // tagsform value
    // const tagsArray = this.tagsForm.value.tags;
    // const tagsString = tagsArray.join(', ');
    // //console.log('tagsString',tagsString);



  }

  callUploadnediaSection(formData: any) {
    console.log('formData==>', formData);

    this.dataService
      .postForm('media/upload', formData)
      .pipe(
        catchError((err) => {
          console.error('Error:', err);
          //     setTimeout(() => {
          //   this.globalService.showToast(err);
          // }, 100);
          return of(null);
        })
      )
      .subscribe((res: any) => {
        console.log('Response:', res);
        // this.getCategoryList();
        // setTimeout(() => {
        //   this.globalService.showToast(res);
        // }, 100);
      });
  }

  // get categories
  getCategoryList() {
    this.categoryListData = {};
    this.dataService
      .get('categories')
      .pipe(
        catchError((err) => {
          console.error('Error:', err);
          return of(null);
        })
      )
      .subscribe((res: any) => {
        let filteredData = [];
        //console.log('Response:', res);
        if (res.data) {
          for (let i = 0; i < res.data.length; i++) {
            const element = res.data[i];
            //console.log('element==>', element.thumbnail);
            if (element?.thumbnail != null) {
              //console.log('environment.API_URL==>', environment.API_URL);
              element.thumbnail = environment.DOMAIN + '/' + element.thumbnail;
            }
            if (this.data?.item) {

              if (element.id == this.data?.item?.category?.id) {
                element.checked = true;
              }
              else {
                element.checked = false;

              }
            }
            filteredData.push(element);
          }
          this.categoryListData.categories = filteredData;
          // Assign categories
          // this.categoryListData.categories.forEach((cat: any) => {
          //   cat.checked = this.data.category.id == cat.id;
          // });

        }
        //console.log('categoryListData==>', this.categoryListData);
        // this.dataSource = this.categoryListData;
        this.cd.detectChanges();
        // this.categoryListData = res.data;
      });
  }

  slugify(value: string): string {
    return value
      .toLowerCase()
      .trim()                              // remove start/end spaces
      .replace(/[–—]/g, '-')               // replace long dashes with normal dash
      .replace(/[^a-z0-9\s-]/g, '')         // remove special characters
      .replace(/\s+/g, '-')                 // spaces → dash
      .replace(/-+/g, '-')                  // multiple dashes → single dash
      .replace(/^-+|-+$/g, '');             // remove dash from start/end
  }

  createPermalink() {
    // Onchange 
    //     const cleanedTitle =
    //   this.specialCharacterHelper.specialCharacterChecker(
    //     this.productDetails.value.productTitle,
    //     {
    //       replaceWithSpace: ['-'],
    //       remove: ['&','*','('],
    //       capitalize: true
    //     }
    //   );
    //   this.productDetails.patchValue({
    //   productTitle: cleanedTitle
    // });

    // onblur 
    const control = this.productDetails.get('productTitle');
    if (!control) return;

    const cleanedTitle =
      this.specialCharacterHelper.specialCharacterChecker(
        control.value,
        // {
        //   replaceWithSpace: ['-'],
        //   remove: ['&', '*', '('],
        //   capitalize: true
        // }
        {
          allowOnly: ['|'],
          replaceWithSpace: ['-'],
          capitalize: true
        }
      );

    control.setValue(cleanedTitle, { emitEvent: false });
    // this.specialCharacterHelper.specialCharacterChecker( 'adjustable--cow|-anti kick',{replaceWithSpace: ['-'],remove: ['|'],capitalize: true});
    // this.productDetails.value.productTitle = this.slugToTitle(this.productDetails.value.productTitle);
    // let permaLinkValue = this.productDetails.value.productTitle.contains(' ')
    const formatted = this.productDetails.value.productTitle.replace(/\s+/g, '-').toLowerCase();
    let cleanSlug = this.slugify(formatted);
    // let limitedSlug = cleanSlug.slice(0, 25);
    cleanSlug = cleanSlug.replace(/-+$/g, '');
    this.permaLink = cleanSlug;
    // this.permaLink = cleanSlug;
    this.seoForm.patchValue({ slugText: formatted });
    this.cd.detectChanges();
  }
  enableInput() {
    this.isInputShow = true;
  }
  getUpdatedValue(event: any) {
    //console.log('event==>', event.target.value);
    let value = event.target.value.replace(/\s+/g, '-').toLowerCase();
    this.permaLink = value;
  }
  onPermalinkBlur(event: any) {
    const value = event.target.textContent.trim().replace(/\s+/g, '-').toLowerCase();
    this.permaLink = value;
    this.cd.detectChanges();
  }
  permalinkAction(action: String) {
    this.isInputShow = false;
    if (action == 'cancel') {
      // this.permaLink = '';
    }
  }
  discountPriceChange() {
    const regularPrice = this.priceSection.value.regularPrice;
    const salePrice = this.priceSection.value.salePrice;
    const discountType = this.priceSection.value.discountType;

    // Check if discount is greater than or equal to regular price (only for Flat discount)
    if (discountType === 'Flat' && regularPrice && salePrice && salePrice >= regularPrice) {
      this.wrongDiscount = true;
    } else {
      this.wrongDiscount = false;
    }
    setTimeout(() => {
      this.getGstDiscount();
    }, 1000);
  }
  onDiscountTypeChange() {
    const discountType = this.priceSection.value.discountType;
    this.updateDiscountValidators(discountType);
    this.discountPriceChange();
  }

  updateDiscountValidators(type: string) {
    const salePriceControl = this.priceSection.get('salePrice');

    if (type === 'Discount') {
      // For percentage, min 0 and max 100
      salePriceControl?.setValidators([Validators.min(0), Validators.max(100)]);
    } else {
      // For flat amount, just min 0
      salePriceControl?.setValidators([Validators.min(0)]);
    }

    salePriceControl?.updateValueAndValidity();

    this.cd.detectChanges();
  }


  removeGalleryImage(index: number) {
    if (!Array.isArray(this.galleryImageEntries) || index < 0 || index >= this.galleryImageEntries.length) {
      return;
    }

    const removedEntry = this.galleryImageEntries[index];
    if (removedEntry?.source === 'existing') {
      if (removedEntry.mediaId !== null) {
        this.removedGalleryMediaIds.add(removedEntry.mediaId);
      }
      this.rememberRemovedGalleryUrl(removedEntry.url);
    } else {
      this.removeSelectedFileByReference(removedEntry?.file);
    }

    this.galleryImageEntries.splice(index, 1);
    this.syncThumbGalleryFromEntries();
    this.rebuildGalleryInputFileList();
    this.productMediaSection?.markAsDirty();
    this.cd.detectChanges();
  }
  removeProductDescriptionGalleryImage(index: number) {
    // Remove from the preview array
    this.prodDescriptionImageGallery.splice(index, 1);

    // Remove from the selected files array
    if (this.selectProductDesciptionImageGallery && this.selectProductDesciptionImageGallery.length > index) {
      this.selectProductDesciptionImageGallery.splice(index, 1);
    }

    // Update the file input with remaining files
    if (this.descriptionImageGallery) {
      if (this.selectProductDesciptionImageGallery.length === 0) {
        // Clear input if no files remain
        this.descriptionImageGallery.nativeElement.value = '';
      } else {
        // Rebuild the FileList with remaining files
        const dataTransfer = new DataTransfer();
        this.selectProductDesciptionImageGallery.forEach((file) => {
          dataTransfer.items.add(file);
        });
        this.descriptionImageGallery.nativeElement.files = dataTransfer.files;
      }
    }

    // Trigger change detection
    this.cd.detectChanges();
  }
  //    uploadPrdouctDescriptionImageGallery(){
  //  if (this.selectedFile?.length) {
  //               for (const file of this.selectedFile) {
  //                 const formData = new FormData();
  //                 formData.append('files', file);
  //                 formData.append('module', 'product');
  //                 formData.append('module_id', id);
  //                 formData.append('type', 'gallery');

  //                 this.callUploadnediaSection(formData);
  //               }
  //             }
  //    }

  resetProductForm() {
    let removeCategory:any = null;
    this.productDetails.reset();
    this.productOptionData.reset();
    this.productMultipleOptionForm.reset();
    this.tagsForm.reset();
    this.productMediaSection.reset();
    this.productInventrySection.reset();
    this.priceSection.reset();
    this.shippingInfoSection.reset();
    this.productAttributesForm.reset();
    this.shippingConfigForm.reset();
    this.offerForm.reset();
    this.seoForm.reset();
    this.permaLink = '';
    this.thumbPreview = '';
    this.selectedFile = [];
    this.galleryImageEntries = [];
    this.removedGalleryMediaIds.clear();
    this.removedGalleryImageUrls.clear();
    this.hasAutoPreviewedUpdateCombinations = false;
    this.isAutoPreviewInProgress = false;
    this.productType = 'simple';
    this.variableCombinations = [];
    this.showVariantTable = false;
    this.selectedAttributesForVariations = [];
    this.pendingVariantImageFiles.clear();
    this.uploadingVariantImageIndexes.clear();
    if (this.variantsReactiveForm) {
      this.variantsFormArray.clear();
    }
    this.thumbGallery = [];
    this.onGetId(removeCategory);
    this.cd.detectChanges();
  }
  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: BeforeUnloadEvent) {
    if (this.hasUnsavedChanges()) {
      $event.preventDefault();
      $event.returnValue = true; // triggers browser alert
    }
  }
  hasUnsavedChanges(): boolean {
    return (
      this.productDetails?.dirty ||
      this.productOptionData?.dirty ||
      this.productMultipleOptionForm?.dirty ||
      this.tagsForm?.dirty ||
      this.productMediaSection?.dirty ||
      this.productInventrySection?.dirty ||
      this.priceSection?.dirty ||
      this.shippingInfoSection?.dirty ||
      this.productAttributesForm?.dirty ||
      this.variantsReactiveForm?.dirty ||
      this.shippingConfigForm?.dirty ||
      this.offerForm?.dirty ||
      this.seoForm?.dirty
    );
  }
}
